import axios from "axios";
import { jwtDecode } from "jwt-decode";

/**
 * FastAPI base URL.
 * - Local HTTPS UI (`npm start`) talks to the CRA proxy (same origin) so
 *   https://localhost:3000 can reach the HTTP API without mixed content.
 * - Production bundle → https://api.sorenkoelbaek.dk
 * Override: REACT_APP_API_URL=https://api.example.com
 */
export const getApiUrl = () => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/$/, "");
  }
  if (process.env.NODE_ENV === "development") {
    return "";
  }
  return "https://api.sorenkoelbaek.dk";
};

const apiClient = axios.create({
  baseURL: getApiUrl(),
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        let decoded;
        try {
          decoded = jwtDecode(refreshToken);
        } catch {
          throw new Error("Invalid refresh token");
        }

        if (decoded.exp * 1000 <= Date.now()) {
          throw new Error("Refresh token expired");
        }

        const response = await axios.post(
          `${getApiUrl()}/auth/refresh-token`,
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } }
        );
        const { access_token, refresh_token } = response.data;

        localStorage.setItem("access_token", access_token);
        if (refresh_token) {
          localStorage.setItem("refresh_token", refresh_token);
        }

        failedQueue.forEach((prom) => prom.resolve(access_token));
        failedQueue = [];

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        failedQueue.forEach((prom) => prom.reject(refreshError));
        failedQueue = [];

        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/login?session_expired=true";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const postInviteUser = (email) =>
  apiClient.post("/auth/invite", { email });

export const getInvitationCodes = async () => {
  const response = await apiClient.get("/auth/invites");
  return response.data;
};

export const revokeInvitationCode = async (codeUuid) => {
  const response = await apiClient.delete(`/auth/invites/${codeUuid}`);
  return response.data;
};

export const postResetInvitePassword = async (codeUuid) => {
  const response = await apiClient.post(`/auth/invites/${codeUuid}/reset-password`);
  return response.data;
};

export const getInvitationInfo = async (code) => {
  const response = await apiClient.get(`/auth/invitation/${encodeURIComponent(code)}`);
  return response.data;
};

export const postRegisterWithCode = ({ username, email, password, code }) =>
  apiClient.post("/auth/register", { username, email, password, code });

export const getSettings = async () => {
  const response = await apiClient.get("/settings");
  return response.data;
};

export const updateSettings = async (payload) => {
  const response = await apiClient.put("/settings", payload);
  return response.data;
};

export const uploadSiteFavicon = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post("/settings/favicon", formData);
  return response.data;
};

export const getPublishedPosts = async (params = {}) => {
  const response = await apiClient.get("/posts", {
    params: { topic: "home", ...params },
  });
  return response.data;
};

export const getCategoryPosts = async (params = {}) => {
  const response = await apiClient.get("/posts", {
    params: { topic: "categories", ...params },
  });
  return response.data;
};

export const getGroundingPosts = async (params = {}) => {
  const response = await apiClient.get("/posts", {
    params: { topic: "grounding", ...params },
  });
  return response.data;
};

export const getWineTastingPosts = async (params = {}) => {
  const response = await apiClient.get("/posts", {
    params: { topic: "wine_tasting", ...params },
  });
  return response.data;
};

export const listTastings = async (params = {}) => {
  const response = await apiClient.get("/tools/tastings", { params });
  return response.data;
};

export const getTasting = async (tastingUuid) => {
  const response = await apiClient.get(`/tools/tastings/${encodeURIComponent(tastingUuid)}`);
  return response.data;
};

export const createTasting = async (payload) => {
  const response = await apiClient.post("/tools/tastings", payload);
  return response.data;
};

export const deleteTasting = async (tastingUuid) => {
  await apiClient.delete(`/tools/tastings/${encodeURIComponent(tastingUuid)}`);
};

export const searchWines = async (q, params = {}) => {
  const response = await apiClient.get("/tools/wines/search", {
    params: { q, ...params },
  });
  return response.data;
};

export const searchWinesByLabel = async (file, params = {}) => {
  const form = new FormData();
  form.append("file", file);
  const response = await apiClient.post("/tools/wines/label-search", form, {
    params,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const getWine = async (wineId) => {
  const response = await apiClient.get(`/tools/wines/${encodeURIComponent(wineId)}`);
  return response.data;
};

export const listWineCollection = async (params = {}) => {
  const response = await apiClient.get("/tools/wine-manager/collection", { params });
  return response.data;
};

export const listWineWantlist = async (params = {}) => {
  const response = await apiClient.get("/tools/wine-manager/wantlist", { params });
  return response.data;
};

export const listWineCountries = async () => {
  const response = await apiClient.get("/tools/wine-manager/countries");
  return response.data;
};

export const listWineGrapes = async (params = {}) => {
  const response = await apiClient.get("/tools/wine-manager/grapes", { params });
  return response.data;
};

export const getUserWine = async (userWineUuid) => {
  const response = await apiClient.get(
    `/tools/wine-manager/items/${encodeURIComponent(userWineUuid)}`
  );
  return response.data;
};

export const addUserWine = async (payload) => {
  const response = await apiClient.post("/tools/wine-manager/items", payload);
  return response.data;
};

export const updateUserWine = async (userWineUuid, payload) => {
  const response = await apiClient.patch(
    `/tools/wine-manager/items/${encodeURIComponent(userWineUuid)}`,
    payload
  );
  return response.data;
};

export const deleteUserWine = async (userWineUuid) => {
  await apiClient.delete(`/tools/wine-manager/items/${encodeURIComponent(userWineUuid)}`);
};

export const getRecipes = async (params = {}) => {
  const response = await apiClient.get("/recipes", { params });
  return response.data;
};

export const createRecipeVariant = async (recipeRef, payload) => {
  const response = await apiClient.post(
    `/recipes/${encodeURIComponent(recipeRef)}/variants`,
    payload
  );
  return response.data;
};

export const preferRecipe = async (recipeRef) => {
  const response = await apiClient.post(
    `/recipes/${encodeURIComponent(recipeRef)}/prefer`
  );
  return response.data;
};

export const getPostTags = async () => {
  const response = await apiClient.get("/posts/tags");
  return response.data;
};

export const searchIngredients = async (q) => {
  const response = await apiClient.get("/ingredients", { params: { q: q || "" } });
  return response.data;
};

export const createIngredient = async (name) => {
  const response = await apiClient.post("/ingredients", { name });
  return response.data;
};

export const getManagedPosts = async () => {
  const response = await apiClient.get("/posts/manage");
  return response.data;
};

export const getPost = async (postRef) => {
  const response = await apiClient.get(`/posts/${encodeURIComponent(postRef)}`);
  return response.data;
};

export const createPost = async (payload) => {
  const response = await apiClient.post("/posts", payload);
  return response.data;
};

export const updatePost = async (postRef, payload) => {
  const response = await apiClient.patch(
    `/posts/${encodeURIComponent(postRef)}`,
    payload
  );
  return response.data;
};

export const deletePost = async (postRef) => {
  await apiClient.delete(`/posts/${encodeURIComponent(postRef)}`);
};

export const getRecipe = async (recipeRef) => {
  const response = await apiClient.get(`/recipes/${encodeURIComponent(recipeRef)}`);
  return response.data;
};

export const createRecipe = async (payload) => {
  const response = await apiClient.post("/recipes", payload);
  return response.data;
};

export const updateRecipe = async (recipeRef, payload) => {
  const response = await apiClient.patch(
    `/recipes/${encodeURIComponent(recipeRef)}`,
    payload
  );
  return response.data;
};

export const deleteRecipe = async (recipeRef) => {
  await apiClient.delete(`/recipes/${encodeURIComponent(recipeRef)}`);
};

export const listPostComments = async (postRef) => {
  const response = await apiClient.get(`/posts/${encodeURIComponent(postRef)}/comments`);
  return response.data;
};

export const createPostComment = async (postRef, payload) => {
  const response = await apiClient.post(
    `/posts/${encodeURIComponent(postRef)}/comments`,
    payload
  );
  return response.data;
};

export const listRecipeComments = async (recipeRef) => {
  const response = await apiClient.get(`/recipes/${encodeURIComponent(recipeRef)}/comments`);
  return response.data;
};

export const createRecipeComment = async (recipeRef, payload) => {
  const response = await apiClient.post(
    `/recipes/${encodeURIComponent(recipeRef)}/comments`,
    payload
  );
  return response.data;
};

export const updateComment = async (commentUuid, payload) => {
  const response = await apiClient.patch(`/comments/${commentUuid}`, payload);
  return response.data;
};

export const deleteComment = async (commentUuid) => {
  await apiClient.delete(`/comments/${commentUuid}`);
};

export const uploadPostImage = async (
  file,
  { postUuid, recipeUuid, description, isFocus = false } = {}
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (postUuid) formData.append("post_uuid", postUuid);
  if (recipeUuid) formData.append("recipe_uuid", recipeUuid);
  if (description) formData.append("description", description);
  if (isFocus) formData.append("is_focus", "true");
  const response = await apiClient.post("/media/images", formData);
  return response.data;
};

export const updateMedia = async (mediaUuid, { description, isFocus } = {}) => {
  const payload = {};
  if (description !== undefined) payload.description = description || null;
  if (isFocus !== undefined) payload.is_focus = Boolean(isFocus);
  const response = await apiClient.patch(`/media/${mediaUuid}`, payload);
  return response.data;
};

/** @deprecated use updateMedia */
export const updateMediaDescription = async (mediaUuid, description) => {
  return updateMedia(mediaUuid, { description });
};

export const deleteMedia = async (mediaUuid) => {
  await apiClient.delete(`/media/${mediaUuid}`);
};

export const uploadAvatar = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post("/auth/me/avatar", formData);
  return response.data;
};

export const getFinanceStatus = async () => {
  const response = await apiClient.get("/finance/status");
  return response.data;
};

export const getFinanceAspsps = async (country) => {
  const response = await apiClient.get("/finance/aspsps", { params: { country } });
  return response.data;
};

export const startFinanceAuth = async (payload) => {
  const response = await apiClient.post("/finance/auth", payload);
  return response.data;
};

export const completeFinanceSession = async (payload) => {
  const response = await apiClient.post("/finance/sessions", payload);
  return response.data;
};

export const getFinanceAccounts = async () => {
  const response = await apiClient.get("/finance/accounts");
  return response.data;
};

export const patchFinanceAccount = async (accountUuid, payload) => {
  const response = await apiClient.patch(`/finance/accounts/${accountUuid}`, payload);
  return response.data;
};

export const syncFinance = async ({ ifStaleHours, force } = {}) => {
  const params = {};
  if (ifStaleHours != null) params.if_stale_hours = ifStaleHours;
  if (force) params.force = true;
  const response = await apiClient.post("/finance/sync", null, { params });
  return response.data;
};

export const getFinanceCategories = async () => {
  const response = await apiClient.get("/finance/categories");
  return response.data;
};

export const createFinanceCategory = async (payload) => {
  const response = await apiClient.post("/finance/categories", payload);
  return response.data;
};

export const updateFinanceCategory = async (categoryUuid, payload) => {
  const response = await apiClient.patch(`/finance/categories/${categoryUuid}`, payload);
  return response.data;
};

export const deleteFinanceCategory = async (categoryUuid) => {
  await apiClient.delete(`/finance/categories/${categoryUuid}`);
};

export const getFinanceRules = async () => {
  const response = await apiClient.get("/finance/rules");
  return response.data;
};

export const previewFinanceRule = async (payload) => {
  const response = await apiClient.post("/finance/rules/preview", payload);
  return response.data;
};

export const createFinanceRule = async (payload) => {
  const response = await apiClient.post("/finance/rules", payload);
  return response.data;
};

export const updateFinanceRule = async (ruleUuid, payload) => {
  const response = await apiClient.patch(`/finance/rules/${ruleUuid}`, payload);
  return response.data;
};

export const deleteFinanceRule = async (ruleUuid) => {
  await apiClient.delete(`/finance/rules/${ruleUuid}`);
};

export const getFinanceTransactions = async (params) => {
  const response = await apiClient.get("/finance/transactions", { params });
  return response.data;
};

export const setFinanceTransactionSplits = async (transactionUuid, splits) => {
  const response = await apiClient.put(`/finance/transactions/${transactionUuid}/splits`, {
    splits,
  });
  return response.data;
};

export const setFinanceTransactionCategory = async (
  transactionUuid,
  categoryUuid,
  exemptFromBudget
) => {
  const payload = { category_uuid: categoryUuid || null };
  if (exemptFromBudget !== undefined) {
    payload.exempt_from_budget = Boolean(exemptFromBudget);
  }
  const response = await apiClient.put(
    `/finance/transactions/${transactionUuid}/category`,
    payload
  );
  return response.data;
};

export const getFinanceSummary = async (params) => {
  const response = await apiClient.get("/finance/summary", { params });
  return response.data;
};

export default apiClient;
